export type CompassHeadingSource = 'webkit' | 'absolute' | 'relative'

export type CompassOrientationEvent = DeviceOrientationEvent & {
  webkitCompassHeading?: number | null
  webkitCompassAccuracy?: number | null
}

export interface CompassHeadingReading {
  heading: number
  source: CompassHeadingSource
  accuracy?: number
}

const FULL_CIRCLE = 360

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value)

export const normalizeAngle = (angle: number) =>
  ((angle % FULL_CIRCLE) + FULL_CIRCLE) % FULL_CIRCLE

export const getShortestAngleDelta = (from: number, to: number) => {
  let delta = normalizeAngle(to) - normalizeAngle(from)

  if (delta > 180) delta -= FULL_CIRCLE
  else if (delta < -180) delta += FULL_CIRCLE

  return delta
}

export const getCompassHeadingPriority = (source: CompassHeadingSource) => {
  if (source === 'webkit') return 3
  if (source === 'absolute') return 2
  return 1
}

export const getScreenOrientationAngle = () => {
  if (typeof window === 'undefined') return 0

  const orientationAngle = window.screen?.orientation?.angle
  if (isFiniteNumber(orientationAngle)) return orientationAngle

  const legacyWindow = window as Window & { orientation?: number }
  if (isFiniteNumber(legacyWindow.orientation)) return legacyWindow.orientation

  return 0
}

export const getCompassHeading = (
  event: CompassOrientationEvent,
  screenOrientationAngle = 0,
): CompassHeadingReading | null => {
  if (isFiniteNumber(event.webkitCompassHeading)) {
    return {
      heading: normalizeAngle(event.webkitCompassHeading),
      source: 'webkit',
      accuracy: isFiniteNumber(event.webkitCompassAccuracy)
        ? event.webkitCompassAccuracy
        : undefined,
    }
  }

  if (!isFiniteNumber(event.alpha)) return null

  const isAbsolute = event.absolute === true || event.type === 'deviceorientationabsolute'

  return {
    heading: normalizeAngle(FULL_CIRCLE - event.alpha + screenOrientationAngle),
    source: isAbsolute ? 'absolute' : 'relative',
  }
}

export const smoothCompassHeading = (
  previousHeading: number | null,
  nextHeading: number,
  smoothingFactor = 0.1,
  deadZoneDegrees = 0.6,
) => {
  if (previousHeading === null) return normalizeAngle(nextHeading)

  const delta = getShortestAngleDelta(previousHeading, nextHeading)
  if (Math.abs(delta) < deadZoneDegrees) return previousHeading

  return previousHeading + delta * smoothingFactor
}
